import { useState, useCallback, useEffect, useRef, useMemo } from 'react';

import Konva from 'konva';
import { useShallow } from 'zustand/react/shallow';

import { useRoomStore, useMagicPenStore } from '@src/stores';
import { useRoomContext, useSettingsContext } from '@src/contexts';

import { IVideoDimensions } from '../VideoBox/videoBox.types';

const useMagicPen = ({
  videoRef,
  videoDimensions,
}: {
  videoRef: React.RefObject<HTMLVideoElement>;
  videoDimensions: IVideoDimensions;
}) => {
  // Local state
  const [isOpen, setIsOpen] = useState(false);
  const [buttonPosition, setButtonPosition] = useState<{ x: number; y: number } | undefined>();
  const [floatingButtonPosition, setFloatingButtonPosition] = useState<{ x: number; y: number } | null>(null);

  // Refs
  const magicPenContainerRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<Konva.Stage>(null);

  // Create stable ref object - useRef ensures this only runs once on mount
  const magicPenCanvasRef = useRef({
    stageRef,
  });

  const { selectedLayout } = useSettingsContext();
  const { canAnnotate, currentPeer } = useRoomContext();

  // Global state from stores
  const { isMagicPenVisible, setIsMagicPenVisible, setIsMagicPenAvailable } = useRoomStore(
    useShallow((state) => ({
      isMagicPenVisible: state.isMagicPenVisible,
      setIsMagicPenVisible: state.setIsMagicPenVisible,
      setIsMagicPenAvailable: state.setIsMagicPenAvailable,
    }))
  );

  const { setActiveTool } = useMagicPenStore(
    useShallow((state) => ({
      setActiveTool: state.setActiveTool,
    }))
  );

  const isMagicPenHidden = useMemo(() => currentPeer?.state?.magic_pen_hidden || false, [currentPeer]);

  // Don't render anything if MagicPen is not visible
  const shouldRender = useMemo(() => isMagicPenVisible, [isMagicPenVisible]);

  // Update stage size based on video dimensions
  const updateStageSize = useCallback(() => {
    const video = videoRef.current;
    if (!video || !videoDimensions.videoWidth) return;

    // Get the video's parent container
    const videoParent = video.parentElement;
    if (!videoParent) return;

    // Position the canvas container absolutely within the video's parent
    if (magicPenContainerRef.current) {
      const canvasContainer = magicPenContainerRef.current;

      // Make sure canvas container is positioned within the same parent
      if (canvasContainer.parentElement !== videoParent) {
        videoParent.appendChild(canvasContainer);
      }

      canvasContainer.style.position = 'absolute';
      canvasContainer.style.width = `${videoDimensions.displayWidth}px`;
      canvasContainer.style.height = `${videoDimensions.displayHeight}px`;
      canvasContainer.style.left = `${videoDimensions.offsetX}px`;
      canvasContainer.style.top = `${videoDimensions.offsetY}px`;
    }

    // Update Konva stage
    if (stageRef.current) {
      const stage = stageRef.current;

      // Set stage dimensions to match video native resolution
      stage.width(videoDimensions.videoWidth);
      stage.height(videoDimensions.videoHeight);

      // Scale the stage to match display size
      stage.scale({ x: videoDimensions.scaleX, y: videoDimensions.scaleY });
      stage.position({ x: 0, y: 0 });

      // Force redraw
      stage.batchDraw();
    }
  }, [videoRef, videoDimensions]); // Depend on videoDimensions

  // Update stage when dimensions or layout changes
  useEffect(() => {
    if (videoDimensions.displayWidth > 0 && videoDimensions.displayHeight > 0) {
      updateStageSize();
    }
  }, [videoDimensions, updateStageSize, selectedLayout]);

  // Set activeTool to 'none' when canAnnotate is false
  useEffect(() => {
    if (!canAnnotate) {
      setActiveTool('none');
    }
  }, [canAnnotate, setActiveTool]);

  // Track component mount/unmount and manage availability
  useEffect(() => {
    setIsMagicPenAvailable(true);
    setIsMagicPenVisible(!isMagicPenHidden);

    return () => {
      setIsMagicPenAvailable(false);
    };
  }, [isMagicPenHidden, setIsMagicPenAvailable, setIsMagicPenVisible]);

  // Reset floating button position when user shows/hides Magic Pen
  useEffect(() => {
    if (!isMagicPenVisible) {
      setIsOpen(false);
    } else {
      setFloatingButtonPosition({ x: 0, y: 0 });
    }
  }, [isMagicPenVisible]);

  // Toggle toolbar visibility and manage positions
  const toggle = useCallback(
    (position?: { x: number; y: number }) => {
      if (position) {
        if (isOpen) {
          // Closing: Update floating button position
          setFloatingButtonPosition((prev) => ({
            x: position.x,
            y: prev?.y || 0,
          }));
        } else {
          // Opening: Set toolbar button position
          setButtonPosition(position);
        }
      }
      setIsOpen((prev) => !prev);
    },
    [isOpen]
  );

  // Handle floating button position changes during drag
  const handleFloatingButtonPositionChange = useCallback((position: { x: number; y: number } | null) => {
    setFloatingButtonPosition(position);
  }, []);

  return {
    // Props
    isOpen,
    buttonPosition,
    floatingButtonPosition,
    shouldRender,
    canAnnotate,

    // Refs
    magicPenContainerRef,
    magicPenCanvasRef,

    // Handlers
    toggle,
    handleFloatingButtonPositionChange,
  };
};

export default useMagicPen;
